import roles from '@/constants/roles';
import { THealthDomainScores } from '@/types/Health.types';
import { TLegalDocumentAcceptanceStatus } from '@/types/Legal.types';
import { TOrganization } from '@/types/Organization.types';
import { TUser } from '@/types/User.types';
import { areRequiredLegalDocumentsAccepted, isEverythingSetup } from '@/utils/onboardingUtils';
import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';

export type TOnboardingStatuses = {
  isUserContactInfoCompleted: boolean;
  isUserDemographicsCompleted: boolean;
  isUserOnboardingCompleted: boolean;
  // isUserOnboardingCompleted => isDemographicsCompleted && isUserContactInfoCompleted.
  // Legal document re-acceptance is tracked separately so policy updates do not reopen onboarding.
  isOrganizationOnboardingCompleted: boolean; // While Organization !== null
  isWholenessScreenerCompleted: boolean;
};

export type TAppDataSetState = {
  // is complete organization data set
  isOrganizationSet: boolean;
  // is complete user data set
  isUserDataSet: boolean;
  isUserContactInfoSet: boolean;
  isUserDemographicSet: boolean;
  // is initial screener completed
  isUserScoresSet: boolean;
  // is EULA accepted
  isEulaAccepted: boolean;
  // latest required legal documents need acceptance
  hasUnacceptedLegalDocuments: boolean;
};

type TAppStoreState = {
  onboardingStatuses: TOnboardingStatuses;
  isOnboardingCompleted: boolean;
  isEulaAccepted: boolean;
  hasUnacceptedLegalDocuments: boolean;
  user: TUser | null;
  organization: TOrganization | null;
  userScores: THealthDomainScores | null;
  legalAcceptanceStatuses: TLegalDocumentAcceptanceStatus[] | null;
  unreadNotifications: number | undefined;
  setUnreadNotifications: (newUnreadNotifications: number) => void;
  setUser: (user: TUser) => void;
  setOrganization: (organization: TOrganization) => void;
  setUserScores: (scores: THealthDomainScores) => void;
  setLegalAcceptanceStatuses: (statuses: TLegalDocumentAcceptanceStatus[]) => void;
  setAppStoreOnSignIn: (
    user: TUser,
    scores: THealthDomainScores,
    legalStatuses: TLegalDocumentAcceptanceStatus[],
    organization: TOrganization | null,
  ) => void;
  reset: () => void;
};

const initialState = {
  onboardingStatuses: {
    isUserContactInfoCompleted: false,
    isUserDemographicsCompleted: false,
    isUserOnboardingCompleted: false,
    isOrganizationOnboardingCompleted: false,
    isWholenessScreenerCompleted: false,
  },
  isOnboardingCompleted: false,
  isEulaAccepted: false,
  hasUnacceptedLegalDocuments: true,
  ///
  user: null,
  organization: null,
  userScores: null,
  legalAcceptanceStatuses: null,
  unreadNotifications: undefined,
};

const isNonEmptyString = (value: unknown) => typeof value === 'string' && value.trim().length > 0;

const hasCompletedUserContactInfo = (user: TUser) =>
  isNonEmptyString(user.user_metadata?.firstName ?? user.firstName) &&
  isNonEmptyString(user.user_metadata?.lastName ?? user.lastName) &&
  isNonEmptyString(user.user_metadata?.phone);

/**
 * NOTE: Example of a store that requires persistence and subscription.
 * State will persis for user even after page refresh.
 * Use this as a reference when creating a new store.
 */
export const useAppStore = create<TAppStoreState>()(
  subscribeWithSelector(
    persist(
      (set) => ({
        ...initialState,
        setUser: (user) => {
          set((state) => {
            const { onboardingStatuses } = state;
            const isAdmin = !!user?.roles.includes(roles.orgAdmin);
            const isContentCreatorOnly = user.roles.includes(roles.contentCreator) && user.roles.length === 1;

            const hasCompletedContactInfo = hasCompletedUserContactInfo(user);

            const isUserDemographicsCompleted = !!user?.user_metadata?.dateOfBirth;

            const isUserOnboardingCompleted = hasCompletedContactInfo && isUserDemographicsCompleted;

            const newOnboardingStatuses = {
              ...onboardingStatuses,
              isUserContactInfoCompleted: hasCompletedContactInfo,
              isUserDemographicsCompleted,
              isUserOnboardingCompleted,
            };

            const isOnboardingCompleted = isEverythingSetup(newOnboardingStatuses, isAdmin, isContentCreatorOnly);

            return {
              ...state,
              onboardingStatuses: newOnboardingStatuses,
              isOnboardingCompleted,
              user,
            };
          });
        },
        setOrganization: (organization) => {
          set((state) => {
            const { onboardingStatuses } = state;

            const isOrganizationSet = !!organization?.representativeEmail && !!organization?.logoUrl;

            const isOrganizationOnboardingCompleted = isOrganizationSet;

            const newOnboardingStatuses = {
              ...onboardingStatuses,
              isOrganizationOnboardingCompleted,
            };

            const isOnboardingCompleted = isEverythingSetup(newOnboardingStatuses);

            return {
              ...state,
              onboardingStatuses: newOnboardingStatuses,
              isOnboardingCompleted,
              organization,
            };
          });
        },
        setUserScores: (scores) => {
          set((state) => {
            const { onboardingStatuses } = state;

            const isWholenessScreenerCompleted = Object.values(scores).reduce((acc, curr) => acc + curr, 0) > 0;
            const isOnboardingCompleted = isEverythingSetup({ ...onboardingStatuses, isWholenessScreenerCompleted });
            return {
              ...state,
              onboardingStatuses: {
                ...onboardingStatuses,
                isWholenessScreenerCompleted,
              },
              isOnboardingCompleted,
              userScores: scores,
            };
          });
        },
        setLegalAcceptanceStatuses: (statuses: TLegalDocumentAcceptanceStatus[]) => {
          set((state) => {
            const isEulaAccepted = areRequiredLegalDocumentsAccepted(statuses);
            const hasUnacceptedLegalDocuments = !isEulaAccepted;

            return {
              ...state,
              legalAcceptanceStatuses: statuses,
              isEulaAccepted,
              hasUnacceptedLegalDocuments,
            };
          });
        },
        setAppStoreOnSignIn: (
          user: TUser,
          scores: THealthDomainScores,
          legalStatuses: TLegalDocumentAcceptanceStatus[],
          organization: TOrganization | null,
        ) => {
          set((state) => {
            const isAdmin = !!user?.roles.includes(roles.orgAdmin);
            const isContentCreatorOnly = user.roles.includes(roles.contentCreator) && user.roles.length === 1;
            const isEulaAccepted = areRequiredLegalDocumentsAccepted(legalStatuses);
            const hasUnacceptedLegalDocuments = !isEulaAccepted;
            const hasCompletedContactInfo = hasCompletedUserContactInfo(user);
            const isUserDemographicsCompleted = !!user?.user_metadata?.dateOfBirth;

            let isOrganizationSet = false;
            if (organization) {
              isOrganizationSet = !!organization?.representativeEmail && !!organization?.logoUrl;
            }

            const statuses = {
              ...state.onboardingStatuses,
              isUserContactInfoCompleted: hasCompletedContactInfo,
              isUserDemographicsCompleted,
              isUserOnboardingCompleted: hasCompletedContactInfo && isUserDemographicsCompleted,
              isOrganizationOnboardingCompleted: isOrganizationSet,
              isWholenessScreenerCompleted: Object.values(scores).reduce((acc, curr) => acc + curr, 0) > 0,
            };

            const isOnboardingCompleted = isEverythingSetup(statuses, isAdmin, isContentCreatorOnly);

            return {
              ...state,
              onboardingStatuses: statuses,
              isOnboardingCompleted,
              user,
              organization,
              userScores: scores,
              legalAcceptanceStatuses: legalStatuses,
              isEulaAccepted,
              hasUnacceptedLegalDocuments,
            };
          });
        },
        setUnreadNotifications: (newUnreadNotifications: number) => {
          set((state) => {
            return {
              ...state,
              unreadNotifications: newUnreadNotifications,
            };
          });
        },
        reset: () => {
          set((state) => ({
            ...state,
            ...initialState,
          }));
        },
      }),
      { name: 'appStore' },
    ),
  ),
);
